/**
 * Profile (feature #10). Shows the user's own users/{uid} details and lets the
 * dealer edit them directly.
 *
 * FREE-TIER MODEL: a dealer may now UPDATE their own `users/{uid}` doc (rules
 * allow it as long as `status`, `email` and `id` are unchanged). So "request a
 * change" is a REAL self-edit written straight to Firestore via
 * `updateOwnUserProfile`. The EMAIL is the verified sign-in identity and is not
 * editable here; the mobile number is a plain profile field and is.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar } from '../../components/AppBar';
import { CenteredScreen } from '../../components/Screen';
import { useAuth } from '../auth/AuthContext';
import { toE164India } from '../../lib/format';
import { deleteOwnAccount, updateOwnUserProfile, type ProfileEdit } from '../../lib/repo';
import { APP_VERSION } from '../../version';
import { isNative } from '../../lib/platform';
import { ROUTES } from '../../routes/paths';

const DEVELOPER_SITE = 'https://mitangs.tech/';
/** Google Play also requires the policy to be reachable from inside the app. */
const PRIVACY_POLICY = 'https://anyway-c1d31.web.app/privacy.html';

/** Open a link in the system browser rather than inside the app shell. */
async function openExternal(url: string): Promise<void> {
  if (isNative) {
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    } catch {
      /* older shells: fall through */
    }
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function ProfileScreen(): JSX.Element {
  const { profile, logout } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ProfileEdit>({});
  // Account deletion is deliberately two-step: one tap arms it, a second
  // confirms. It is irreversible, so a stray tap must not be enough.
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (!profile) {
    return (
      <div className="flex min-h-full flex-1 flex-col">
        <AppBar />
        <CenteredScreen title="Profile">
          <p className="text-sm text-slate-500">No profile loaded.</p>
        </CenteredScreen>
      </div>
    );
  }

  const rows: Array<[string, string | undefined]> = [
    ['Name', profile.name],
    ['Mobile', profile.mobile],
    ['Email', profile.email],
    ['Company', profile.companyName],
    ['City', profile.city],
    ['GST', profile.gstNumber],
  ];

  const startEditing = () => {
    setForm({
      name: profile.name,
      mobile: profile.mobile,
      companyName: profile.companyName,
      city: profile.city,
      gstNumber: profile.gstNumber ?? '',
    });
    setSaved(false);
    setError(null);
    setEditing(true);
  };

  const saveChanges = async () => {
    setError(null);
    const mobile = toE164India(form.mobile ?? '');
    if (!mobile) return setError('Enter a valid mobile number.');
    if (!form.companyName?.trim()) return setError('Company name is required.');
    if (!form.city?.trim()) return setError('City is required.');

    // Self-update the caller's own users/{uid} doc (status/email untouched → allowed).
    const edit: ProfileEdit = {
      name: form.name?.trim() ?? '',
      mobile,
      companyName: form.companyName.trim(),
      city: form.city.trim(),
      gstNumber: form.gstNumber?.trim() ? form.gstNumber.trim() : '',
    };
    setBusy(true);
    try {
      await updateOwnUserProfile(profile.id, edit);
      // AuthContext's live listener reflects the new values automatically.
      setSaved(true);
      setEditing(false);
    } catch {
      setError('Could not save your changes. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const setField = (key: keyof ProfileEdit) => (value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  return (
    <div className="flex min-h-full flex-1 flex-col">
      <AppBar />
      <div className="flex-1 overflow-y-auto p-4">
        <div className="rounded-2xl bg-white ring-1 ring-slate-100">
          <dl className="divide-y divide-slate-100">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-4 px-4 py-3 text-sm">
                <dt className="text-slate-500">{label}</dt>
                <dd className="text-right font-medium text-slate-800">{value ?? '—'}</dd>
              </div>
            ))}
          </dl>
        </div>

        {editing ? (
          <div className="mt-4 space-y-3 rounded-2xl bg-white p-4 ring-1 ring-slate-100">
            <EditField label="Full name (optional)" value={form.name ?? ''} onChange={setField('name')} />
            <EditField label="Mobile number" value={form.mobile ?? ''} onChange={setField('mobile')} type="tel" />
            <EditField label="Company name" value={form.companyName ?? ''} onChange={setField('companyName')} />
            <EditField label="City" value={form.city ?? ''} onChange={setField('city')} />
            <EditField label="GST number (optional)" value={form.gstNumber ?? ''} onChange={setField('gstNumber')} />
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-primary flex-1"
                disabled={busy}
                onClick={() => void saveChanges()}
              >
                {busy ? 'Saving…' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn-secondary flex-1"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            {saved ? (
              <p className="mt-4 rounded-xl bg-green-50 px-4 py-3 text-sm text-drop">
                Your details have been updated.
              </p>
            ) : null}
            <button
              type="button"
              className="btn-secondary mt-4 w-full"
              onClick={startEditing}
            >
              Edit my details
            </button>
          </>
        )}

        <button
          type="button"
          className="btn-secondary mt-3 w-full"
          onClick={async () => {
            await logout();
            navigate(ROUTES.login, { replace: true });
          }}
        >
          Log out
        </button>

        {/* DANGER ZONE — Google Play requires an in-app way for a user to delete
            their own account and the data behind it. Kept visually quiet but
            genuinely reachable, which is what the policy asks for. */}
        <div className="mt-8 rounded-2xl border border-red-100 bg-red-50/50 p-4">
          <p className="text-[12px] font-bold uppercase tracking-wide text-red-700">
            Delete my account
          </p>
          <p className="mt-1 text-[12px] leading-relaxed text-slate-600">
            Permanently removes your profile, price access and activity history, and signs you out
            of every device. This cannot be undone — you would have to register again.
          </p>

          {deleteError && (
            <p className="mt-3 rounded-lg bg-red-100 px-3 py-2 text-[12px] font-medium text-red-800">
              {deleteError}
            </p>
          )}

          {!confirmDelete ? (
            <button
              type="button"
              className="mt-3 w-full rounded-xl border border-red-300 bg-white px-4 py-2.5 text-[13px] font-bold text-red-700"
              onClick={() => {
                setDeleteError(null);
                setConfirmDelete(true);
              }}
            >
              Delete my account
            </button>
          ) : (
            <div className="mt-3 space-y-2">
              <p className="text-[12.5px] font-bold text-red-800">
                Are you sure? This is permanent.
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-[13px] font-bold text-slate-600"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="flex-1 rounded-xl bg-red-600 px-3 py-2.5 text-[13px] font-bold text-white disabled:opacity-60"
                  disabled={deleting}
                  onClick={async () => {
                    setDeleting(true);
                    setDeleteError(null);
                    try {
                      await deleteOwnAccount();
                      // The Firebase account is gone; drop the local session and
                      // land on login rather than a screen with no profile.
                      await logout().catch(() => undefined);
                      navigate(ROUTES.login, { replace: true });
                    } catch (e) {
                      setDeleteError(e instanceof Error ? e.message : 'Could not delete your account.');
                      setDeleting(false);
                      setConfirmDelete(false);
                    }
                  }}
                >
                  {deleting ? 'Deleting…' : 'Delete permanently'}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-[11px] text-slate-400">
          ANYWAY IT SOLUTIONS v{APP_VERSION}
        </p>
        <p className="mt-1 text-center text-[11px] text-slate-400">
          Developed by{' '}
          <button
            type="button"
            className="font-semibold text-brand underline"
            onClick={() => void openExternal(DEVELOPER_SITE)}
          >
            Mitang Hindocha
          </button>
        </p>
        <p className="mt-2 text-center text-[11px] text-slate-400">
          <button
            type="button"
            className="underline"
            onClick={() => void openExternal(PRIVACY_POLICY)}
          >
            Privacy policy
          </button>
        </p>
      </div>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}): JSX.Element {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        className="field"
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
